import type SelfEntity from '@/entity/SelfEntity';
import { i18n } from '@/i18n';
import router, { addRoutes } from '@/router/index.router';
import SignalRTagUser from '@/signalr/TagUser.signalr';
import { USER_TYPE } from '@/typings/enums/USER_TYPE';
import { Reactive } from '@/utils/decorators';
import sessionManager from '@/utils/SessionManager';
import { useLoader, useNotif } from '@orion.ui/orion';
import useAuthService from './AuthService';
import useFingerprintService from './FingerprintService';
import { useMixpanelService } from './MixpanelService';

class SelfService {

	@Reactive private readonly state = { selfEntity: undefined as Undef<SelfEntity> };

	get self (): SelfEntity { return (this.state.selfEntity ?? { isConnected: false }) as SelfEntity }
	get isAgency () { return this.self.isAgency() }
	get isClient () { return this.self.isClient() }
	get isTempWorker () { return this.self.isTempWorker() }

	constructor (selfEntity?: SelfEntity) {
		if (selfEntity) this.setSelfEntity(selfEntity);
	}

	setSelfEntity (selfEntity: SelfEntity) {
		this.state.selfEntity = selfEntity;
		return this.self;
	}

	async loginAsync (payload: { login: string, password: string }, visitorData?: VisitorData) {
		useLoader().show(i18n.global.t('auth.account.login'));

		try {
			if (!visitorData) {
				visitorData = await useFingerprintService().getFingerprintAsync();
			}

			const data = await useAuthService().signInAsync({
				login: payload.login,
				password: payload.password,
				...visitorData,
			});

			localStorage.setItem('userType', data.userType.toString());

			useMixpanelService().identify(data.userId);
			useMixpanelService().track('user.login', {
				userType: data.userType,
				loginMethod: 'direct',
			});

			const getTokenAsync = async () => {
				await sessionManager.getTokenAsync();
			};

			addRoutes(data.userType);

			if (data.userType !== USER_TYPE.TempWorker) {
				SignalRTagUser.onTagUserUpdate(getTokenAsync);
			}

			if (router.currentRoute?.value?.query?.redirect) {
				window.location.href = router.currentRoute.value.query.redirect as string;
				return;
			}

			router.push({ name: 'Dashboard' });
		}
		catch (error: any) {
			const errorMessage = error?.response?.data;
			if (typeof errorMessage === 'string') {
				if (errorMessage.includes('minutes')) {
					useNotif.danger({
						message: errorMessage,
						duration: 0,
					});
					return i18n.global.t('common.accountBlocked');
				}

				if (errorMessage.includes('multiples tentatives incorrectes')) {
					return i18n.global.t('common.accountBlocked');
				}

				// useNotif.warning(errorMessage);
			}
			else if (typeof errorMessage === 'object') {
				if (errorMessage?.message.includes('Valider la connexion')) {
					return errorMessage;
				}
			}
			else {
				useNotif.warning(i18n.global.t('common.notification.incorrectCredentials'));
			}
		}
		finally {
			useLoader().hide();
		}
	}

}

const selfServiceSingleton = new SelfService();

export default function useSelfService () {
	return selfServiceSingleton;
}
